import React from 'react'
import { IcaDialog } from '../IcaElements/IcaDialog'
import { IcaHeader } from '../IcaElements/IcaHeader'
import { IcaSegment } from '../IcaElements/IcaSegment'
import { IcaRadio } from '../IcaElements/IcaRadio'
import { IcaInputNumberStepper } from '../IcaElements/IcaInputNumberStepper'
import { IcaButton } from '../IcaElements/IcaButton'
import { IcaButtonIcon } from '../IcaElements/IcaButtonIcon'
import { IcaMeta } from '../IcaElements/IcaMeta'
import { getRunningOutThreshold, getRunningOutThresholdTooltip } from '../../utils/stockUtils'
import { IcaTooltip } from '../IcaElements/IcaTooltip'
import { IcaCardRow } from '../IcaElements/IcaCardRow'
import StoreGroceryStock from '../../types/StoreGroceryStock'
import '@ica-azure/ica-elements/IcaIconInfo'
import './GroceryStockSettingsDialog.scss'


interface Props {
  open: boolean
  groceryItem: StoreGroceryStock
  onClose: () => void
  onConfirm: (threshold?: number, maxQuantity?: number) => Promise<void> | void
  defaultRunningOutThreshold: number | null
  defaultMaxQuantity: number | null
}

export default function GroceryStockSettingsDialog({ open, onClose, onConfirm, defaultRunningOutThreshold, defaultMaxQuantity, groceryItem }: Props) {
  const [thresholdMode, setThresholdMode] = React.useState<'default' | 'custom'>('default')
  const [maxQuantityMode, setMaxQuantityMode] = React.useState<'default' | 'custom'>('default')
  const [runningOutThresholdValue, setRunningOutThresholdValue] = React.useState<number | null>(defaultRunningOutThreshold)
  const [maxQuantity, setMaxQuantity] = React.useState<number | null>(defaultMaxQuantity)
  const [thresholdInputError, setThresholdInputError] = React.useState<string | null>(null)
  const [maxQuantityInputError, setMaxQuantityInputError] = React.useState<string | null>(null)
  
  const computedThreshold = React.useMemo(() => {
    const ref = maxQuantityMode === 'custom'
      ? (maxQuantity ?? defaultMaxQuantity ?? groceryItem?.restockedQuantity ?? 0)
      : (groceryItem?.restockedQuantity ?? 0)
    return getRunningOutThreshold(ref)
  }, [groceryItem, maxQuantity, defaultMaxQuantity, maxQuantityMode])

  const tooltipText = React.useMemo(() => {
    const maxParam = maxQuantityMode === 'custom' ? (maxQuantity ?? defaultMaxQuantity ?? null) : undefined
    return getRunningOutThresholdTooltip(groceryItem?.restockedQuantity ?? 0, groceryItem?.quantity ?? 0, maxParam)
  }, [groceryItem, maxQuantity, defaultMaxQuantity, maxQuantityMode])

  React.useEffect(() => {
    if (open) {
      const maxQuantityMode = defaultMaxQuantity != null ? 'custom' : 'default'
      const maxQuantity = defaultMaxQuantity != null ? defaultMaxQuantity : null

      setThresholdMode(defaultRunningOutThreshold != null ? 'custom' : 'default')
      setMaxQuantityMode(maxQuantityMode)
      setMaxQuantity(maxQuantity)

      const thresholdRef = maxQuantityMode === 'custom'
        ? (maxQuantity ?? defaultMaxQuantity ?? groceryItem?.restockedQuantity ?? 0)
        : (groceryItem?.restockedQuantity ?? 0)

      setRunningOutThresholdValue(defaultRunningOutThreshold != null ? defaultRunningOutThreshold : getRunningOutThreshold(thresholdRef))
    }
  }, [open, defaultRunningOutThreshold, defaultMaxQuantity, groceryItem])

  const handleConfirm = async () => {
    if (thresholdMode === 'custom' && (runningOutThresholdValue == null || runningOutThresholdValue <= 0)) {
      setThresholdInputError('Gränsvärdet måste vara större än 0')
      return
    }

    if (maxQuantityMode === 'custom' && (maxQuantity == null || maxQuantity <= 0)) {
      setMaxQuantityInputError('Maxsaldo måste vara större än 0')
      return
    }

    const thresholdToSend = thresholdMode === 'default' ? undefined : (runningOutThresholdValue ?? undefined)
    const maxQuantityToSend = maxQuantityMode === 'default' ? undefined : (maxQuantity ?? undefined)

    await onConfirm(thresholdToSend, maxQuantityToSend)
    setThresholdInputError(null)
    setMaxQuantityInputError(null)
    onClose()
  }

  return (
    <IcaDialog open={open} onClose={onClose} slot="main" snaptotop>
      <IcaHeader slot="header" variant="dialog" heading="Saldoinställningar" leftaction="close" subheading={groceryItem.name} onLeftactionclick={onClose} />
      <IcaSegment heading={'Maxsaldo'} />
      <div className="mt-2">
        <div 
          className="custom-radio-row"
          onClick={() => { setMaxQuantityMode('default'); setMaxQuantity(null) }}
        >
          <div className="flex items-center gap-2 flex-1">
            <IcaRadio checked={maxQuantityMode === 'default'} onChange={() => { setMaxQuantityMode('default'); setMaxQuantity(null) }} />
            <IcaMeta heading={'Inget maxsaldo'} />
          </div>
        </div>
        <IcaRadio checked={maxQuantityMode === 'custom'} onChange={() => { setMaxQuantityMode('custom'); setMaxQuantity(defaultMaxQuantity ?? 1) }} row >
          <IcaMeta heading="Ange eget maxsaldo" subheading="Högsta antal som ska finnas av artikeln i skåpet" />
        </IcaRadio>
        {maxQuantityMode === 'custom' && (
          <>
            <IcaCardRow noninteractive>
              <span>Antal</span>
              <IcaInputNumberStepper
                min={1}
                value={maxQuantity ?? defaultMaxQuantity ?? 1}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMaxQuantityInputError(null)
                  const newValue = Number(event.target.value)
                  setMaxQuantity(newValue)
                }}
              />
            </IcaCardRow>
            {maxQuantityInputError && <p className="text-[var(--color-accent-red)] bg-[var(--color-white)] px-4 py-2">{maxQuantityInputError}</p>}
          </>
        )}
      </div>
      <IcaSegment heading={'Gränsvärde innan "Snart slut"'} />
      <div className="mt-2">
        <div 
          className="custom-radio-row"
          onClick={() => setThresholdMode('default')}
        >
          <div className="flex items-center gap-2 flex-1">
            <IcaRadio checked={thresholdMode === 'default'} onChange={() => setThresholdMode('default')} />
            <IcaMeta heading={`När det är ${computedThreshold} kvar`} subheading={maxQuantityMode === 'custom' ? `Av maxsaldo ${maxQuantity}` : `Eftersom det senaste påfyllda saldot är ${groceryItem?.restockedQuantity ?? 0}`} />
          </div>
          <div className="flex items-center">
            <IcaTooltip position="left">
              <IcaButtonIcon slot="trigger">
                <tgdb-ica-icon-info style={{ fill: 'var(--color-grey-mid)', color: 'var(--color-grey-mid)' }} />
              </IcaButtonIcon>
              <p slot="content">{tooltipText}</p>
            </IcaTooltip>
          </div>
        </div>
        <IcaRadio checked={thresholdMode === 'custom'} onChange={() => setThresholdMode('custom')} row>
          <IcaMeta heading="Ange eget gränsvärde" subheading='Gränsvärdet styr när artikeln visas som "Snart slut"' />
        </IcaRadio>
        {thresholdMode === 'custom' && (
          <>
            <IcaCardRow noninteractive>
              <span>Antal kvar</span>
              <IcaInputNumberStepper
                min={1}
                value={runningOutThresholdValue ?? undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setThresholdInputError(null)
                  const newValue = Number(event.target.value)
                  setRunningOutThresholdValue(newValue)
                }}
              />
            </IcaCardRow>
            {thresholdInputError && <p className="text-[var(--color-accent-red)] bg-[var(--color-white)] px-4 py-2">{thresholdInputError}</p>}
          </>
        )}
      </div>
      <div className="bg-[var(--color-white)]">
        <div className="my-4 px-4">
          <IcaButton text="Klar" onClick={handleConfirm} fullwidth />
        </div>
      </div>
    </IcaDialog>
  )
}